/* @flow */

import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ParentalControlExtendedItem } from '../../../helpers/parentalControl/constsAndTypes';

import { logError } from '../../../helpers/debug/debug';
import { PictoInfo, PictoRecord } from '../../pictos/Element';
import './RestrictionSelector.css';

type RestrictionSelectorPropType = {
  +initialMinAge: number,
  +items: Array<ParentalControlExtendedItem>,
  +onMinAgeChange: (value: number) => Promise<void> | void,
};

const findSelectedIndex = (items: Array<ParentalControlExtendedItem>, maxAgeAllowed: number) => {
  const index = items.findIndex(({ minAge }) => minAge === maxAgeAllowed);
  if (index > -1) {
    return index;
  }

  for (let i = 0; i < items.length; i++) {
    const {
      [i]: { minAge },
    } = items;

    if (minAge > maxAgeAllowed) {
      return i;
    }
  }

  // Default value is there for linter purpose, but this code should never be reached
  return items.length - 1;
};

const RestrictionSelector = ({ initialMinAge, items, onMinAgeChange }: RestrictionSelectorPropType): React.Node => {
  const [selectedIndex, setSelectedIndex] = useState(() => findSelectedIndex(items, initialMinAge));
  const wrapperElt = useRef<HTMLElement | null>(null);

  useEffect(() => {
    wrapperElt.current?.style.setProperty('--selected-index', selectedIndex.toString());
  }, [selectedIndex]);

  const handleOnClick = useCallback(
    async (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      const { currentTarget } = event;

      const dataIndex = currentTarget.getAttribute('data-index');
      const dataMinAge = currentTarget.getAttribute('data-min-age');

      const newIndex = parseInt(dataIndex, 10);
      const newMinAge = parseInt(dataMinAge, 10);
      if (!isNaN(newMinAge) && !isNaN(newIndex) && newIndex !== selectedIndex && newIndex >= 0 && newIndex <= items.length) {
        setSelectedIndex(newIndex);

        const result = onMinAgeChange(newMinAge);

        if (result instanceof Promise) {
          result.catch((error) => {
            logError(`Error in RestrictionSelector onMinAgeChange: ${error.message}`);
          });
        }
      }
    },
    [items, onMinAgeChange, selectedIndex],
  );

  return (
    <>
      <div className='pcItemsWrapper' ref={wrapperElt}>
        {items.map(({ label, minAge }, i) => (
          <button
            aria-label={label}
            className={clsx('pcItem', i < selectedIndex && 'included', i === selectedIndex && 'selected')}
            data-index={i}
            data-min-age={minAge}
            key={minAge}
            onClick={handleOnClick}
            type='button'
          >
            <div className='pictoElementBg'>
              <PictoRecord />
            </div>
            <div className='pcLabel'>{label}</div>
          </button>
        ))}
      </div>
      <div className='pcVideoMinAgeHint'>
        <PictoInfo />
        <div className=''>{items[selectedIndex].text}</div>
      </div>
    </>
  );
};

export default RestrictionSelector;
